import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../components/AppShell";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { fmtINR, fmtPct, longDate } from "../lib/format";
import { useToast } from "../hooks/use-toast";
import {
  Loader2, Upload, FileText, AlertCircle, TrendingUp, TrendingDown,
  Briefcase, Search, RefreshCw,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { stampedName } from "../lib/csv";
import ExportButtons from "../components/ExportButtons";
import StatCard from "../components/StatCard";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend,
} from "recharts";

const CAT_COLORS = { equity: "#10b981", mutual_fund: "#6366f1" };
const CAT_LABEL = { equity: "Equity", mutual_fund: "Mutual Funds" };

// ----- CSV parser for Zerodha holdings statement format ----- //
// Default / preferred format: "Combined Holdings Statement as on YYYY-MM-DD"
//   - single table mixing equity & mutual funds
//   - columns: Symbol, ISIN, Sector, Instrument Type, Quantity Available,
//     Quantity Discrepant, Quantity Long Term, Quantity Pledged (Margin),
//     Quantity Pledged (Loan), Average Price, Previous Closing Price,
//     Unrealized P&L, Unrealize P&L Pct.
//
// Also still supports the legacy split format with separate
// "Mutual Funds Holdings Statement" / "Equity Holdings Statement" sections.
//
// Per-row category detection:
//   - ISIN prefix "INF" → mutual_fund (Indian MF ISIN convention)
//   - else if Instrument Type is set & not "-" → mutual_fund (e.g. "Equity - Sectoral/Thematic")
//   - else → equity
//
// IMPORTANT: `quantity` stored = Quantity Available + Quantity Pledged (Margin).
// This is the user's defined "Total Quantity" used for invested / present / P&L.
function parseZerodhaCsv(text) {
  const lines = text.split(/\r?\n/);

  // Tiny CSV line splitter (quoted commas supported)
  const splitCsv = (line) => {
    const out = [];
    let cur = "", q = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (q) {
        if (c === '"' && line[i + 1] === '"') { cur += '"'; i++; }
        else if (c === '"') q = false;
        else cur += c;
      } else {
        if (c === '"') q = true;
        else if (c === ",") { out.push(cur.trim()); cur = ""; }
        else cur += c;
      }
    }
    out.push(cur.trim());
    return out;
  };

  // The Zerodha export has a leading empty column on every row — strip it so
  // index lookups line up with the user-visible column positions.
  const rows = lines
    .map(splitCsv)
    .map((r) => (r.length > 1 && r[0] === "" ? r.slice(1) : r));

  let statementDate = null;
  let clientId = null;
  const summary = {};
  const holdings = [];
  const errors = [];

  // Section state
  let currentCategory = null; // "mutual_fund" | "equity" | "combined" (auto-detect per row)
  let headerIdx = null; // indexes for the expected columns in the current section
  let isCombined = false;

  const norm = (s) =>
    String(s || "").toLowerCase().replace(/\s+/g, "").replace(/[().,&]/g, "");

  const findIdx = (headers, name) => {
    const n = norm(name);
    return headers.findIndex((h) => norm(h) === n);
  };

  // Per-row category resolver — used in combined mode.
  const detectCategory = (isin, instrumentType) => {
    const isinUp = String(isin || "").toUpperCase();
    if (isinUp.startsWith("INF")) return "mutual_fund";
    const it = String(instrumentType || "").trim();
    if (it && it !== "-") return "mutual_fund";
    return "equity";
  };

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const joined = row.join("|").trim();
    if (!joined.replace(/\|/g, "")) continue; // blank

    const first = (row[0] || "").trim();
    const firstLower = first.toLowerCase();

    if (firstLower.startsWith("client id")) {
      clientId = (row[1] || "").trim();
      continue;
    }

    // Section / report headers
    if (firstLower.includes("combined holdings statement")) {
      currentCategory = "combined";
      isCombined = true;
      const m = first.match(/(\d{4}-\d{2}-\d{2})/);
      if (m) statementDate = m[1];
      headerIdx = null;
      continue;
    }
    if (firstLower.includes("mutual funds holdings statement")) {
      currentCategory = "mutual_fund";
      isCombined = false;
      const m = first.match(/(\d{4}-\d{2}-\d{2})/);
      if (m) statementDate = m[1];
      headerIdx = null;
      continue;
    }
    if (firstLower.includes("equity holdings statement") ||
        (firstLower.includes("holdings statement") && !firstLower.includes("mutual") && !firstLower.includes("combined"))) {
      currentCategory = "equity";
      isCombined = false;
      const m = first.match(/(\d{4}-\d{2}-\d{2})/);
      if (m && !statementDate) statementDate = m[1];
      headerIdx = null;
      continue;
    }
    if (firstLower === "summary") {
      headerIdx = null;
      // Consume following key-value pairs until we hit a non-summary row
      // (i.e. another section header or a blank-after-data). Blank rows
      // between the "Summary" heading and the first key are tolerated.
      let seenAny = false;
      for (let j = i + 1; j < rows.length; j++) {
        const r = rows[j];
        const key = (r[0] || "").trim();
        const val = (r[1] || "").trim();
        if (!key && !val) {
          if (seenAny) { i = j; break; }
          continue; // skip leading blank between heading and first kv
        }
        if (!key) continue;
        // Stop if this row looks like the next section / column header
        const kl = key.toLowerCase();
        if (kl === "symbol" || kl.startsWith("client id") ||
            kl.includes("holdings statement")) {
          i = j - 1;
          break;
        }
        const num = parseFloat(val.replace(/[,₹\s]/g, ""));
        if (kl.includes("invested")) summary.investedValue = num;
        else if (kl.includes("present")) summary.presentValue = num;
        else if (kl.includes("pct")) summary.unrealizedPnLPct = num;
        else if (kl.includes("p&l") || kl.includes("unrealized")) summary.unrealizedPnL = num;
        seenAny = true;
        i = j;
      }
      continue;
    }

    // Column header row (Symbol,ISIN,...)
    if (firstLower === "symbol") {
      headerIdx = {
        symbol: findIdx(row, "Symbol"),
        isin: findIdx(row, "ISIN"),
        sector: findIdx(row, "Sector"),
        instrumentType: findIdx(row, "Instrument Type"),
        quantityAvailable: findIdx(row, "Quantity Available"),
        quantityDiscrepant: findIdx(row, "Quantity Discrepant"),
        quantityLongTerm: findIdx(row, "Quantity Long Term"),
        quantityPledgedMargin: findIdx(row, "Quantity Pledged Margin"),
        quantityPledgedLoan: findIdx(row, "Quantity Pledged Loan"),
        avgPrice: findIdx(row, "Average Price"),
        lastPrice: findIdx(row, "Previous Closing Price"),
        unrealizedPnL: findIdx(row, "Unrealized P&L"),
        unrealizedPnLPct: findIdx(row, "Unrealize P&L Pct"),
      };
      // Accept "Unrealized P&L Pct" too (legacy header name)
      if (headerIdx.unrealizedPnLPct < 0) {
        headerIdx.unrealizedPnLPct = findIdx(row, "Unrealized P&L Pct");
      }
      continue;
    }

    // Data row
    if (headerIdx && currentCategory && first) {
      const n = (v) => {
        const parsed = parseFloat(String(v || "").replace(/[,₹\s]/g, ""));
        return isNaN(parsed) ? 0 : parsed;
      };
      const cell = (k) => (headerIdx[k] >= 0 ? row[headerIdx[k]] : "");
      const sectorVal = (cell("sector") || "").trim();
      const itVal = (cell("instrumentType") || "").trim();
      const isinVal = cell("isin");

      // In combined mode, derive category per row. Otherwise use the section.
      const category = isCombined
        ? detectCategory(isinVal, itVal)
        : currentCategory;

      const qtyAvail = n(cell("quantityAvailable"));
      const qtyPledgedMargin = n(cell("quantityPledgedMargin"));

      // For equity rows we prefer Sector as the displayable "type" label,
      // since their Instrument Type is "-". For MF rows we keep the real type.
      let displayType = itVal && itVal !== "-" ? itVal : sectorVal;
      if (!displayType || displayType === "-") displayType = "";

      holdings.push({
        symbol: cell("symbol"),
        isin: isinVal,
        sector: sectorVal && sectorVal !== "-" ? sectorVal : "",
        instrumentType: displayType,
        category,
        // ⬇⬇ TOTAL QUANTITY = Quantity Available + Quantity Pledged (Margin)
        // This is what drives invested/present/P&L everywhere downstream.
        quantity: +(qtyAvail + qtyPledgedMargin).toFixed(6),
        quantityAvailable: qtyAvail,
        quantityDiscrepant: n(cell("quantityDiscrepant")),
        quantityLongTerm: n(cell("quantityLongTerm")),
        quantityPledgedMargin: qtyPledgedMargin,
        quantityPledgedLoan: n(cell("quantityPledgedLoan")),
        avgPrice: n(cell("avgPrice")),
        lastPrice: n(cell("lastPrice")),
        unrealizedPnL: n(cell("unrealizedPnL")),
        unrealizedPnLPct: n(cell("unrealizedPnLPct")),
      });
    }
  }

  if (holdings.length === 0) {
    errors.push(
      "No holdings rows detected. Expected a Zerodha 'Combined Holdings Statement' (or legacy Equity/MF statement) with a Symbol, ISIN, … header row.",
    );
  }

  return { holdings, summary, statementDate, clientId, errors };
}

function CategoryBadge({ category }) {
  const isMf = category === "mutual_fund";
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded border whitespace-nowrap ${
      isMf
        ? "bg-indigo-500/10 text-indigo-300 border-indigo-500/30"
        : "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
    }`}>
      {CAT_LABEL[category] || category}
    </span>
  );
}

export default function Portfolio() {
  const { user } = useAuth();
  const { toast } = useToast();
  const isAdmin = user?.role === "admin";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all");
  const [sortBy, setSortBy] = useState("currentValue");

  // Upload state
  const [parsed, setParsed] = useState(null);
  const [parseErrors, setParseErrors] = useState([]);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshReport, setRefreshReport] = useState(null);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const d = await api.holdings();
      setData(d);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to load holdings");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const rows = data?.holdings || [];
    return rows
      .filter((h) => (tab === "all" ? true : h.category === tab))
      .filter((h) => h.symbol.toLowerCase().includes(q.toLowerCase()))
      .map((h) => {
        const invested = h.avgPrice * h.quantity;
        const current = h.lastPrice * h.quantity;
        const pnl = current - invested;
        const pnlPct = invested > 0 ? (pnl / invested) * 100 : 0;
        return { ...h, invested, current, pnl, pnlPct };
      })
      .sort((a, b) => {
        if (sortBy === "symbol") return a.symbol.localeCompare(b.symbol);
        if (sortBy === "pnlPct") return b.pnlPct - a.pnlPct;
        if (sortBy === "pnl") return b.pnl - a.pnl;
        return b.current - a.current; // currentValue default
      });
  }, [data, q, tab, sortBy]);

  const onFile = async (f) => {
    setFile(f);
    setParsed(null);
    setParseErrors([]);
    if (!f) return;
    try {
      const text = await f.text();
      const r = parseZerodhaCsv(text);
      setParsed(r);
      setParseErrors(r.errors);
    } catch (e) {
      setParseErrors([`Failed to parse CSV: ${e.message || e}`]);
    }
  };

  const submitUpload = async () => {
    if (!parsed?.holdings?.length) return toast({ title: "No rows to upload" });
    if (!window.confirm(
      `This will REPLACE all existing holdings with ${parsed.holdings.length} new rows from the CSV. Continue?`
    )) return;
    setUploading(true);
    try {
      await api.uploadHoldings({
        rows: parsed.holdings,
        summary: parsed.summary,
        statementDate: parsed.statementDate,
        source: "zerodha_csv",
      });
      toast({
        title: "Holdings uploaded",
        description: `${parsed.holdings.length} holdings imported.`,
      });
      setFile(null);
      setParsed(null);
      await load();
    } catch (e) {
      toast({ title: "Upload failed", description: e.response?.data?.detail || "Could not upload" });
    }
    setUploading(false);
  };

  const exportData = useMemo(() => {
    const headers = [
      "Symbol", "ISIN", "Instrument Type", "Category",
      "Total Qty", "Qty Available", "Qty Pledged (Margin)",
      "Avg Price", "Last Price", "Invested", "Current", "P&L", "P&L %",
    ];
    const rows = filtered.map((h) => [
      h.symbol, h.isin || "", h.instrumentType || "", h.category,
      h.quantity, h.quantityAvailable ?? "", h.quantityPledgedMargin ?? "",
      h.avgPrice, h.lastPrice, h.invested, h.current, h.pnl, (h.pnlPct || 0).toFixed(2),
    ]);
    const pdfColumns = ["Symbol", "Category", "Total Qty", "Avg", "LTP", "Invested", "Current", "P&L", "P&L %"];
    const pdfRows = filtered.map((h) => [
      h.symbol,
      CAT_LABEL[h.category] || h.category,
      h.quantity.toFixed(h.quantity < 10 ? 4 : 2),
      "₹" + h.avgPrice.toFixed(2),
      "₹" + h.lastPrice.toFixed(2),
      fmtINR(h.invested, { decimals: 0 }),
      fmtINR(h.current, { decimals: 0 }),
      fmtINR(h.pnl, { decimals: 0 }),
      (h.pnlPct || 0).toFixed(2) + "%",
    ]);
    return { headers, rows, pdfColumns, pdfRows };
  }, [filtered]);

  const refreshPrices = async () => {    setRefreshing(true);
    setRefreshReport(null);
    try {
      const res = await api.refreshHoldingsPrices();
      setRefreshReport(res);
      const msg = res.failed > 0
        ? `${res.updated} updated, ${res.failed} failed`
        : `${res.updated}/${res.total} holdings refreshed`;
      toast({
        title: "Prices refreshed",
        description: msg,
      });
      await load();
    } catch (e) {
      toast({ title: "Refresh failed", description: e.response?.data?.detail || "Could not refresh prices" });
    }
    setRefreshing(false);
  };

  const downloadTemplate = () => {
    const today = new Date().toISOString().slice(0, 10);
    // Combined Holdings Statement — default format going forward.
    // Each row is preceded by an empty leading column to mirror Zerodha's export.
    const csv = [
      ",,,,,,,,,,,,,",
      ",Client ID,DEMO123,,,,,,,,,,,",
      ",,,,,,,,,,,,,",
      `,Combined Holdings Statement as on ${today},,,,,,,,,,,,`,
      ",,,,,,,,,,,,,",
      ",Summary,,,,,,,,,,,,",
      ",Invested Value,100000,,,,,,,,,,,",
      ",Present Value,105000,,,,,,,,,,,",
      ",Unrealized P&L,5000,,,,,,,,,,,",
      ",Unrealized P&L Pct.,5.0,,,,,,,,,,,",
      ",,,,,,,,,,,,,",
      ",Symbol,ISIN,Sector,Instrument Type,Quantity Available,Quantity Discrepant,Quantity Long Term,Quantity Pledged (Margin),Quantity Pledged (Loan),Average Price,Previous Closing Price,Unrealized P&L,Unrealize P&L Pct.",
      ",RELIANCE,INE002A01018,ENERGY,-,10,0,0,5,0,2450.50,2500.00,495.00,1.0202",
      ",TCS,INE467B01029,SOFTWARE SERVICES,-,8,0,0,0,0,3200.00,3300.00,800.00,3.1250",
      ",TATA ETHICAL FUND - DIRECT PLAN,INF277K01NG4,-,Equity - Sectoral/Thematic,240.378,0,-,0,0,415.99,420.00,964.30,0.96",
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "holdings_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading)
    return (
      <AppShell>
        <div className="flex items-center py-24 text-slate-400">
          <Loader2 className="animate-spin mr-2" /> Loading holdings…
        </div>
      </AppShell>
    );

  const s = data?.summary || {};
  const pnlPositive = (s.unrealizedPnL || 0) >= 0;
  const pieData = [
    { name: "Equity", value: s.byCategory?.equity || 0, key: "equity" },
    { name: "Mutual Funds", value: s.byCategory?.mutual_fund || 0, key: "mutual_fund" },
  ].filter((d) => d.value > 0);

  return (
    <AppShell>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="inline-flex items-center gap-2 text-[10px] text-emerald-300/85 uppercase tracking-[0.22em] font-mono">
            <span className="pulse-dot" style={{ width: 6, height: 6 }} />
            Portfolio
          </div>
          <h1 className="text-2xl md:text-4xl font-display text-white mt-1 tracking-tight">Equity & Mutual Fund Holdings</h1>
          {data?.meta?.statementDate && (
            <div className="text-xs text-slate-400 mt-1">
              Statement as on <span className="text-slate-200">{longDate(data.meta.statementDate)}</span>
              {data?.meta?.updatedAt && (
                <span> · uploaded {longDate(data.meta.updatedAt.slice(0, 10))}</span>
              )}
              {data?.meta?.lastRefreshAt && (
                <span> · prices refreshed {new Date(data.meta.lastRefreshAt).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
              )}
            </div>
          )}
        </div>
        {(data?.summary?.count || 0) > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <ExportButtons
              testIdPrefix="portfolio-export"
              csv={{
                filename: stampedName("portfolio_holdings"),
                headers: exportData.headers,
                rows: exportData.rows,
              }}
              pdf={{
                title: "Portfolio holdings",
                subtitle: data?.meta?.statementDate
                  ? `Statement as on ${longDate(data.meta.statementDate)}`
                  : undefined,
                filename: "portfolio_holdings",
                orientation: "landscape",
                columns: exportData.pdfColumns,
                rows: exportData.pdfRows,
                summary: [
                  ["Invested value", fmtINR(s.investedValue || 0)],
                  ["Present value", fmtINR(s.presentValue || 0)],
                  ["Unrealized P&L", `${fmtINR(s.unrealizedPnL || 0)} (${fmtPct(s.unrealizedPnLPct || 0)})`],
                  ["Holdings", `${s.count || 0} (${s.equityCount || 0} eq · ${s.mutualFundCount || 0} mf)`],
                ],
              }}
            />
            {isAdmin && (
              <Button
                onClick={refreshPrices}
                disabled={refreshing}
                data-testid="refresh-prices-btn"
                className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold"
              >
                <RefreshCw size={14} className={`mr-2 ${refreshing ? "animate-spin" : ""}`} />
                {refreshing ? "Fetching…" : "Refresh live prices"}
              </Button>
            )}
          </div>
        )}
      </div>

      {err && (
        <div className="mt-5 text-rose-400 bg-rose-500/10 border border-rose-500/30 rounded-md px-4 py-3 text-sm">
          {err}
        </div>
      )}

      {/* Summary cards */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <StatCard
          icon={Briefcase}
          label="Invested Value"
          value={fmtINR(s.investedValue || 0, { compact: true })}
          sub={fmtINR(s.investedValue || 0)}
        />
        <StatCard
          icon={TrendingUp}
          label="Present Value"
          value={fmtINR(s.presentValue || 0, { compact: true })}
          sub={fmtINR(s.presentValue || 0)}
        />
        <StatCard
          icon={pnlPositive ? TrendingUp : TrendingDown}
          label="Unrealized P&L"
          value={fmtINR(s.unrealizedPnL || 0, { compact: true })}
          sub={fmtINR(s.unrealizedPnL || 0)}
          positive={pnlPositive}
          tone={pnlPositive ? "emerald" : "rose"}
          valueClassName={pnlPositive ? "text-emerald-400" : "text-rose-400"}
        />
        <StatCard
          icon={TrendingUp}
          label="Return %"
          value={fmtPct(s.unrealizedPnLPct || 0)}
          sub={`${s.count || 0} holdings · ${s.equityCount || 0} eq · ${s.mutualFundCount || 0} mf`}
          positive={pnlPositive}
          tone={pnlPositive ? "emerald" : "rose"}
          valueClassName={pnlPositive ? "text-emerald-400" : "text-rose-400"}
        />
      </div>

      {/* Admin upload area */}
      {isAdmin && (
        <div className="mt-6 bg-slate-900 border border-slate-800 rounded-xl p-5" data-testid="portfolio-upload-card">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-2">
                <Upload size={16} className="text-amber-400" />
                <div className="text-sm font-semibold text-white">Upload holdings CSV</div>
              </div>
              <div className="text-xs text-slate-500 mt-1">
                Upload your Zerodha <span className="text-slate-300">Combined Holdings Statement</span> CSV
                (equity &amp; mutual funds in one file). Total Qty = Quantity Available + Quantity Pledged (Margin). Replaces all existing holdings.
              </div>
            </div>
            <button
              type="button"
              onClick={downloadTemplate}
              data-testid="portfolio-template-btn"
              className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1.5 px-3 py-2 rounded-md border border-slate-800 hover:border-emerald-500/50"
            >
              <FileText size={12} /> Download template
            </button>
          </div>

          <div className="mt-4 flex flex-col md:flex-row gap-3 items-start">
            <Input
              type="file"
              accept=".csv,text/csv"
              data-testid="portfolio-file-input"
              onChange={(e) => onFile(e.target.files?.[0] || null)}
              className="bg-slate-950 border-slate-800 text-slate-300 file:mr-3 file:rounded-md file:border-0 file:bg-amber-500 file:text-slate-950 file:px-3 file:py-1.5 file:text-xs file:font-semibold hover:file:bg-amber-400 cursor-pointer"
            />
            <Button
              onClick={submitUpload}
              disabled={uploading || !parsed || parsed.holdings.length === 0 || parseErrors.length > 0}
              data-testid="portfolio-upload-submit"
              className="h-10 px-5 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold disabled:opacity-50"
            >
              <Upload size={14} className="mr-2" />
              {uploading ? "Uploading…" : parsed ? `Replace with ${parsed.holdings.length} holdings` : "Upload"}
            </Button>
          </div>

          {parseErrors.length > 0 && (
            <div className="mt-3 bg-rose-500/10 border border-rose-500/30 rounded-md p-3 text-xs text-rose-300">
              <div className="flex items-center gap-2 font-semibold mb-1">
                <AlertCircle size={12} /> Parse issues
              </div>
              <ul className="space-y-0.5">{parseErrors.map((e, i) => <li key={i}>· {e}</li>)}</ul>
            </div>
          )}

          {parsed && parsed.holdings.length > 0 && (
            <div className="mt-3 text-xs text-slate-400">
              Preview: <span className="text-slate-200">{parsed.holdings.length}</span> holdings parsed
              {parsed.statementDate && <span> · statement date <span className="text-slate-200">{parsed.statementDate}</span></span>}
              {parsed.summary?.investedValue && <span> · invested <span className="text-slate-200">{fmtINR(parsed.summary.investedValue)}</span></span>}
              {parsed.summary?.presentValue && <span> · present <span className="text-slate-200">{fmtINR(parsed.summary.presentValue)}</span></span>}
            </div>
          )}
        </div>
      )}

      {/* Refresh report */}
      {refreshReport && (
        <div className="mt-4 bg-slate-900 border border-slate-800 rounded-xl p-4" data-testid="refresh-report">
          <div className="flex items-start justify-between flex-wrap gap-2">
            <div>
              <div className="text-sm font-semibold text-white">Price refresh report</div>
              <div className="text-xs text-slate-500">
                <span className="text-emerald-400">{refreshReport.updated} updated</span>
                {refreshReport.failed > 0 && <span> · <span className="text-rose-400">{refreshReport.failed} failed</span></span>}
                <span className="text-slate-500"> · AMFI for MFs (ISIN) · Yahoo Finance for equities (NSE/BSE)</span>
              </div>
            </div>
            <button onClick={() => setRefreshReport(null)} className="text-slate-500 hover:text-white text-xs px-2">Dismiss</button>
          </div>
          {refreshReport.results?.some((r) => r.status !== "ok") && (
            <div className="mt-3 max-h-40 overflow-y-auto border border-slate-800 rounded-md">
              <table className="w-full text-xs">
                <thead className="bg-slate-950 text-slate-400 sticky top-0">
                  <tr>
                    <th className="text-left p-2">Symbol</th>
                    <th className="text-left p-2">Status</th>
                    <th className="text-left p-2">Reason</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {refreshReport.results.filter((r) => r.status !== "ok").map((r, i) => (
                    <tr key={i}>
                      <td className="p-2 text-slate-300">{r.symbol}</td>
                      <td className="p-2 text-rose-400">{r.status}</td>
                      <td className="p-2 text-slate-500">{r.reason || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Allocation chart */}
      {pieData.length > 0 && (
        <div className="mt-6 bg-slate-900 border border-slate-800 rounded-xl p-5">
          <div className="text-sm font-semibold text-white">Asset Allocation</div>
          <div className="text-xs text-slate-500">Split by instrument category (present value)</div>
          <div className="mt-4 h-56">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                  stroke="#0f172a"
                >
                  {pieData.map((entry) => (
                    <Cell key={entry.key} fill={CAT_COLORS[entry.key]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12, color: "#e2e8f0" }}
                  formatter={(v) => fmtINR(v)}
                />
                <Legend wrapperStyle={{ fontSize: 12, color: "#e2e8f0" }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mt-6 flex flex-wrap gap-2 items-center">
        {[
          { k: "all", l: `All (${s.count || 0})` },
          { k: "equity", l: `Equity (${s.equityCount || 0})` },
          { k: "mutual_fund", l: `Mutual Funds (${s.mutualFundCount || 0})` },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`portfolio-tab-${t.k}`}
            className={`px-3 py-1.5 rounded-md text-xs font-medium border ${
              tab === t.k
                ? "bg-emerald-500 text-slate-950 border-emerald-500"
                : "bg-slate-900 text-slate-400 border-slate-800 hover:text-white"
            }`}
          >
            {t.l}
          </button>
        ))}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={14} />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search symbol…"
            className="pl-9 bg-slate-900 border-slate-800 text-white placeholder:text-slate-500"
          />
        </div>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="bg-slate-900 border border-slate-800 text-white text-sm rounded-md px-3 h-10"
        >
          <option value="currentValue">Sort: Current Value</option>
          <option value="pnl">Sort: P&amp;L</option>
          <option value="pnlPct">Sort: P&amp;L %</option>
          <option value="symbol">Sort: Symbol A-Z</option>
        </select>
      </div>

      {/* Holdings table */}
      <div className="mt-4 bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
        {filtered.length === 0 && (
          <div className="text-sm text-slate-500 py-12 text-center">
            No holdings yet. {isAdmin && "Upload your Zerodha CSV above to populate."}
          </div>
        )}
        {filtered.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-900/80 text-[11px] text-slate-400 uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Symbol</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">Type</th>
                  <th className="text-right px-4 py-3" title="Total Qty = Quantity Available + Quantity Pledged (Margin)">Total Qty</th>
                  <th className="text-right px-4 py-3 hidden sm:table-cell">Avg Price</th>
                  <th className="text-right px-4 py-3 hidden sm:table-cell">LTP</th>
                  <th className="text-right px-4 py-3 hidden md:table-cell">Invested</th>
                  <th className="text-right px-4 py-3">Current</th>
                  <th className="text-right px-4 py-3">P&amp;L</th>
                  <th className="text-right px-4 py-3 hidden sm:table-cell">P&amp;L %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {filtered.map((h) => {
                  const pos = h.pnl >= 0;
                  const qAvail = h.quantityAvailable ?? h.quantity;
                  const qPledged = h.quantityPledgedMargin || 0;
                  const fmtQ = (q) => q.toFixed(q !== 0 && Math.abs(q) < 10 ? 4 : 2);
                  return (
                    <tr key={h.id || h.symbol} className="hover:bg-slate-800/50 transition-colors" data-testid={`holding-row-${h.symbol}`}>
                      <td className="px-4 py-3">
                        <div className="text-white font-medium text-sm leading-tight">{h.symbol}</div>
                        <div className="mt-1 flex items-center gap-2 flex-wrap">
                          <CategoryBadge category={h.category} />
                          {h.isin && <span className="text-[10px] text-slate-500 font-mono">{h.isin}</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell text-slate-400 text-xs max-w-[180px] truncate">
                        {h.instrumentType || h.sector || "—"}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs">
                        <div className="text-slate-200" data-testid={`holding-total-qty-${h.symbol}`}>{fmtQ(h.quantity)}</div>
                        {qPledged > 0 && (
                          <div className="text-[10px] text-slate-500 mt-0.5 leading-tight">
                            {fmtQ(qAvail)} avail · {fmtQ(qPledged)} pledged
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right text-slate-400 font-mono text-xs hidden sm:table-cell">
                        ₹{h.avgPrice.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-right text-slate-300 font-mono text-xs hidden sm:table-cell">
                        ₹{h.lastPrice.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-right text-slate-400 hidden md:table-cell">
                        {fmtINR(h.invested)}
                      </td>
                      <td className="px-4 py-3 text-right text-white font-medium">
                        {fmtINR(h.current)}
                      </td>
                      <td className={`px-4 py-3 text-right font-medium ${pos ? "text-emerald-400" : "text-rose-400"}`}>
                        {fmtINR(h.pnl)}
                      </td>
                      <td className={`px-4 py-3 text-right text-xs font-medium hidden sm:table-cell ${pos ? "text-emerald-400" : "text-rose-400"}`}>
                        {fmtPct(h.pnlPct)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppShell>
  );
}

function SummaryCard({ icon: Icon, label, value, sub, positive }) {
  return (
    <StatCard
      icon={Icon}
      label={label}
      value={value}
      sub={sub}
      positive={positive}
      tone={positive === false ? "rose" : "emerald"}
      valueClassName={
        positive === true ? "text-emerald-400" : positive === false ? "text-rose-400" : ""
      }
    />
  );
}
